/** Compliance readiness for Admin fleet/driver scan (CONF-20260718). */

export type ComplianceReadiness =
  | 'expired'
  | 'due_soon'
  | 'incomplete'
  | 'ready';

export const COMPLIANCE_DUE_SOON_DAYS = 30;

export type ComplianceSummary = {
  expired: number;
  dueSoon: number;
  incomplete: number;
  ready: number;
};

/** Local calendar YYYY-MM-DD (not UTC). */
export function toLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addLocalDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toLocalDateString(dt);
}

function hasText(value: string | null | undefined): boolean {
  return !!value && value.trim().length > 0;
}

function expiryBucket(
  expiry: string | null | undefined,
  today: string
): 'expired' | 'due_soon' | null {
  if (!hasText(expiry)) return null;
  const day = expiry!.trim().slice(0, 10);
  if (day < today) return 'expired';
  const until = addLocalDays(today, COMPLIANCE_DUE_SOON_DAYS);
  if (day <= until) return 'due_soon';
  return null;
}

export function vehicleComplianceReadiness(
  vehicle: {
    registration_plate?: string | null;
    registration_expiry?: string | null;
    status?: string | null;
  },
  today: string = toLocalDateString()
): ComplianceReadiness {
  const bucket = expiryBucket(vehicle.registration_expiry, today);
  if (bucket === 'expired') return 'expired';
  if (bucket === 'due_soon') return 'due_soon';

  const plateOk = hasText(vehicle.registration_plate);
  const active = vehicle.status === 'active';
  if (!plateOk || (active && !hasText(vehicle.registration_expiry))) {
    return 'incomplete';
  }
  return 'ready';
}

export function driverComplianceReadiness(
  driver: {
    license_expiry?: string | null;
  },
  today: string = toLocalDateString()
): ComplianceReadiness {
  if (!hasText(driver.license_expiry)) return 'incomplete';
  const bucket = expiryBucket(driver.license_expiry, today);
  if (bucket === 'expired') return 'expired';
  if (bucket === 'due_soon') return 'due_soon';
  return 'ready';
}

export function isExpiryPast(
  expiry: string | null | undefined,
  today: string = toLocalDateString()
): boolean {
  return expiryBucket(expiry, today) === 'expired';
}

/**
 * Dispatch hard-block (CONF-20260719): expiry day itself is not dispatchable.
 * Unknown/null expiry is not blocked here (Incomplete remains assignable).
 */
export function isDispatchBlockedByExpiry(
  expiry: string | null | undefined,
  onDate: string
): boolean {
  if (!hasText(expiry)) return false;
  return expiry!.trim().slice(0, 10) <= onDate;
}

export type ComplianceDispatchErrorKind =
  | 'DRIVER_LICENSE_EXPIRED'
  | 'VEHICLE_REGISTRATION_EXPIRED'
  | null;

/** Parse Postgres RAISE EXCEPTION codes from trip assign / publish / platform RPCs. */
export function parseComplianceDispatchError(message: string): {
  kind: ComplianceDispatchErrorKind;
  expiry?: string;
  onDate?: string;
} {
  const driver = /DRIVER_LICENSE_EXPIRED:([^:]+):([^:]*):([0-9-]+)/.exec(message);
  if (driver) {
    return { kind: 'DRIVER_LICENSE_EXPIRED', expiry: driver[2] || undefined, onDate: driver[3] };
  }
  const vehicle = /VEHICLE_REGISTRATION_EXPIRED:([^:]+):([^:]*):([0-9-]+)/.exec(message);
  if (vehicle) {
    return {
      kind: 'VEHICLE_REGISTRATION_EXPIRED',
      expiry: vehicle[2] || undefined,
      onDate: vehicle[3],
    };
  }
  return { kind: null };
}

/** Summary chips: active rows only (CONF Q3). */
export function summarizeActiveCompliance(
  rows: readonly { status?: string | null; readiness: ComplianceReadiness }[]
): ComplianceSummary {
  const summary: ComplianceSummary = {
    expired: 0,
    dueSoon: 0,
    incomplete: 0,
    ready: 0,
  };
  for (const row of rows) {
    if (row.status !== 'active') continue;
    switch (row.readiness) {
      case 'expired':
        summary.expired += 1;
        break;
      case 'due_soon':
        summary.dueSoon += 1;
        break;
      case 'incomplete':
        summary.incomplete += 1;
        break;
      case 'ready':
        summary.ready += 1;
        break;
      default: {
        const _exhaustive: never = row.readiness;
        void _exhaustive;
      }
    }
  }
  return summary;
}

export function complianceTagColor(
  readiness: ComplianceReadiness
): 'error' | 'warning' | 'default' | 'success' {
  switch (readiness) {
    case 'expired':
      return 'error';
    case 'due_soon':
      return 'warning';
    case 'incomplete':
      return 'default';
    case 'ready':
      return 'success';
    default: {
      const _exhaustive: never = readiness;
      return _exhaustive;
    }
  }
}

/** Lower rank = more urgent (default list sort). */
export function complianceReadinessRank(readiness: ComplianceReadiness): number {
  switch (readiness) {
    case 'expired':
      return 0;
    case 'due_soon':
      return 1;
    case 'incomplete':
      return 2;
    case 'ready':
      return 3;
    default: {
      const _exhaustive: never = readiness;
      return _exhaustive;
    }
  }
}
